<template>
  <component
      :is="tag"
      :class="chipClasses"
      :style="customStyle"
      @click="onClick"
  >
    <md-icon v-if="icon" class="chip-icon">{{ icon }}</md-icon>
    <span class="chip-content">
      <slot>{{ label }}</slot>
    </span>
    <span v-if="count !== null && count !== undefined" class="chip-count">
      {{ count }}
    </span>
  </component>
</template>

<script>
export default {
  name: 'Chip',
  props: {
    /**
     * Semantic color variant.
     * One of: primary | success | danger | warning | secondary | info
     */
    type: {
      type: String,
      default: 'primary',
      validator: function (value) {
        return [
          'primary', 'success', 'danger', 'error',
          'warning', 'secondary', 'info'
        ].indexOf(value) !== -1;
      }
    },

    /** Optional material icon name (e.g. 'business') */
    icon: {
      type: String,
      default: ''
    },

    /** Plain text label when no default slot is provided */
    label: {
      type: [String, Number],
      default: ''
    },

    /** Numeric badge displayed on the right (e.g. count) */
    count: {
      type: [String, Number],
      default: null
    },

    /** Render as a different HTML element (button, span, a, div) */
    tag: {
      type: String,
      default: 'span'
    },

    /** Compact vs default size */
    size: {
      type: String,
      default: 'default',
      validator: function (value) {
        return ['default', 'small', 'large'].indexOf(value) !== -1;
      }
    },

    /** Outlined variant (transparent background, colored border/text) */
    outlined: {
      type: Boolean,
      default: false
    },

    /** Clickable (adds hover + cursor pointer) */
    clickable: {
      type: Boolean,
      default: false
    },

    /** Optional inline style overrides */
    customStyle: {
      type: [String, Object],
      default: null
    }
  },

  computed: {
    /** Normalize `error` alias to `danger` */
    normalizedType: function () {
      return this.type === 'error' ? 'danger' : this.type;
    },

    chipClasses: function () {
      return [
        'chip',
        'chip--' + this.normalizedType,
        'chip--' + this.size,
        {
          'chip--outlined': this.outlined,
          'chip--clickable': this.clickable
        }
      ];
    }
  },

  methods: {
    onClick: function (event) {
      if (this.clickable) {
        this.$emit('click', event);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
/* ============ BASE CHIP ============ */
.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
  vertical-align: middle;
  transition: background-color 0.15s ease, color 0.15s ease;

  .chip-icon {
    font-size: 14px !important;
    width: 14px !important;
    height: 14px !important;
    flex-shrink: 0;
    line-height: 1;
  }

  .chip-content {
    display: inline-flex;
    align-items: center;
  }

  .chip-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 18px;
    padding: 0 6px;
    margin-left: 4px;
    background: rgba(255, 255, 255, 0.35);
    border-radius: 9px;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
  }
}

/* ============ SIZES ============ */
.chip--small {
  padding: 2px 8px;
  font-size: 11px;

  .chip-icon {
    font-size: 12px !important;
    width: 12px !important;
    height: 12px !important;
  }

  .chip-count {
    min-width: 16px;
    height: 16px;
    font-size: 9px;
  }
}

.chip--large {
  padding: 5px 14px;
  font-size: 13px;

  .chip-icon {
    font-size: 16px !important;
    width: 16px !important;
    height: 16px !important;
  }

  .chip-count {
    min-width: 22px;
    height: 20px;
    font-size: 11px;
  }
}

/* ============ COLOR VARIANTS ============ */
.chip--primary {
  background: #e8eaf6;
  color: #3949ab;

  .chip-count {
    background: rgba(57, 73, 171, 0.15);
    color: #3949ab;
  }
}

.chip--success {
  background: #e8f5e9;
  color: #2e7d32;

  .chip-count {
    background: rgba(46, 125, 50, 0.15);
    color: #2e7d32;
  }
}

.chip--danger {
  background: #ffebee;
  color: #c62828;

  .chip-count {
    background: rgba(198, 40, 40, 0.15);
    color: #c62828;
  }
}

.chip--warning {
  background: #fff3e0;
  color: #e65100;

  .chip-count {
    background: rgba(230, 81, 0, 0.15);
    color: #e65100;
  }
}

.chip--secondary {
  background: #f5f5f5;
  color: #616161;

  .chip-count {
    background: rgba(97, 97, 97, 0.15);
    color: #616161;
  }
}

.chip--info {
  background: #e3f2fd;
  color: #1565c0;

  .chip-count {
    background: rgba(21, 101, 192, 0.15);
    color: #1565c0;
  }
}

/* ============ OUTLINED VARIANT ============ */
.chip--outlined {
  background: transparent !important;
  border: 1px solid currentColor;

  &.chip--primary { color: #3949ab; }
  &.chip--success { color: #2e7d32; }
  &.chip--danger  { color: #c62828; }
  &.chip--warning { color: #e65100; }
  &.chip--secondary { color: #616161; }
  &.chip--info    { color: #1565c0; }

  .chip-count {
    background: rgba(0, 0, 0, 0.08);
    color: inherit;
  }
}

/* ============ CLICKABLE ============ */
.chip--clickable {
  cursor: pointer;
  border: none;
  outline: none;

  &:hover {
    filter: brightness(0.95);
  }

  &:active {
    transform: scale(0.97);
  }
}
</style>